import React, { useCallback } from "react";
import {
	BAND_HEX_COLORS,
	BAND_LABEL,
	DIAL_SEGMENTS,
	SEGMENT_BAND_MAP,
} from "./RatingDial.constants";

interface RatingDialProps {
	value: number | null;
	onChange: (score: number) => void;
	disabled?: boolean;
	size?: "sm" | "md" | "lg";
	showLabel?: boolean;
	previousValue?: number | null;
}

const SIZE_CLASSES = {
	sm: { button: "h-8 w-8 text-xs", gap: "gap-1" },
	md: { button: "h-9 w-9 text-sm", gap: "gap-1.5" },
	lg: { button: "h-10 w-10 text-base", gap: "gap-2" },
} as const;

export const RatingDial: React.FC<RatingDialProps> = ({
	value,
	onChange,
	disabled = false,
	size = "md",
	showLabel = true,
	previousValue,
}) => {
	const classes = SIZE_CLASSES[size];

	const handleKeyDown = useCallback(
		(e: React.KeyboardEvent) => {
			if (disabled) {
				return;
			}

			let newValue = value ?? 0;

			switch (e.key) {
				case "ArrowRight":
				case "ArrowUp":
					e.preventDefault();
					newValue = Math.min((value ?? 0) + 1, 9);
					break;
				case "ArrowLeft":
				case "ArrowDown":
					e.preventDefault();
					newValue = Math.max((value ?? 2) - 1, 1);
					break;
				case "Home":
					e.preventDefault();
					newValue = 1;
					break;
				case "End":
					e.preventDefault();
					newValue = 9;
					break;
				default:
					return;
			}

			if (newValue >= 1 && newValue <= 9) {
				onChange(newValue);
			}
		},
		[disabled, onChange, value]
	);

	const currentBand = value ? SEGMENT_BAND_MAP[value - 1] : null;
	const currentLabel = currentBand ? BAND_LABEL[currentBand] : null;
	const currentColor = currentBand ? BAND_HEX_COLORS[currentBand] : undefined;

	return (
		<div
			className="inline-flex flex-col items-center"
			data-testid="rating-dial"
		>
			<div
				className={`flex ${classes.gap}`}
				role="radiogroup"
				aria-label="Performance rating"
				onKeyDown={handleKeyDown}
				tabIndex={disabled ? -1 : 0}
			>
				{Array.from({ length: DIAL_SEGMENTS }, (_, i) => {
					const score = i + 1;
					const band = SEGMENT_BAND_MAP[i];
					const bandColor = BAND_HEX_COLORS[band];
					const isSelected = value === score;
					const isPrevious = previousValue === score && !isSelected;

					return (
						<button
							key={score}
							type="button"
							onClick={() => {
								if (!disabled) {
									onChange(score);
								}
							}}
							disabled={disabled}
							className={`${classes.button} flex items-center justify-center rounded-full font-medium transition-all ${
								isSelected
									? "text-white shadow-sm ring-2 ring-offset-1"
									: "bg-gray-100 hover:opacity-80"
							} ${disabled ? "cursor-default opacity-50" : "cursor-pointer"}`}
							style={
								isSelected
									? {
											backgroundColor: bandColor,
											boxShadow: `0 0 0 2px ${bandColor}`,
										}
									: isPrevious
										? {
												borderWidth: 2,
												borderColor: "#9CA3AF",
												borderStyle: "dashed",
												color: "#6B7280",
												backgroundColor: "#F9FAFB",
											}
										: { color: bandColor }
							}
							role="radio"
							aria-checked={isSelected}
							aria-label={`Score ${score} - ${BAND_LABEL[band]}`}
							data-testid={`dial-segment-${score}`}
						>
							{score}
						</button>
					);
				})}
			</div>
			{showLabel && currentLabel && (
				<span
					className="mt-1 text-xs font-medium"
					style={{ color: currentColor }}
					data-testid="dial-band-label"
				>
					{currentLabel}
				</span>
			)}
		</div>
	);
};
